import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RideBroadcast, RideStatus, MyCheckinItem } from '@ridemaadi/types';
import { apiFetch } from '../../lib/api-client';
import { rideKeys } from './hooks';

interface Progress {
  meetingPointId: string;
  checkedInCount: number;
}

export interface SosAlert {
  id: string;
  rideId: string;
  userId: string;
  userName: string;
  status: 'active' | 'cleared';
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  clearedAt: string | null;
  responseCount: number;
}

export const activityApi = {
  listBroadcasts: (rideId: string) =>
    apiFetch<{ items: RideBroadcast[] }>(`/rides/${rideId}/broadcasts`),
  sendBroadcast: (rideId: string, message: string) =>
    apiFetch<RideBroadcast>(`/rides/${rideId}/broadcasts`, { method: 'POST', body: { message } }),
  changeStatus: (
    rideId: string,
    status: RideStatus,
    reason?: string,
    startLocation?: { latitude: number; longitude: number },
  ) =>
    apiFetch(`/rides/${rideId}/status`, {
      method: 'POST',
      body: {
        status,
        reason,
        ...(startLocation
          ? { startedLatitude: startLocation.latitude, startedLongitude: startLocation.longitude }
          : {}),
      },
    }),
  checkinProgress: (rideId: string) =>
    apiFetch<{ items: Progress[] }>(`/rides/${rideId}/checkins/progress`),
  myCheckins: (rideId: string) =>
    apiFetch<{ items: MyCheckinItem[]; meetingPointIds: string[] }>(`/rides/${rideId}/checkins/mine`),
  checkIn: (
    rideId: string,
    payload: {
      meetingPointId: string;
      latitude: number;
      longitude: number;
      checkinMethod?: 'manual' | 'geofence';
    },
  ) =>
    apiFetch(`/rides/${rideId}/checkins`, {
      method: 'POST',
      body: {
        meetingPointId: payload.meetingPointId,
        latitude: payload.latitude,
        longitude: payload.longitude,
        checkinMethod: payload.checkinMethod ?? 'manual',
      },
    }),
  mySos: (rideId: string) => apiFetch<{ alert: SosAlert | null }>(`/rides/${rideId}/sos/mine`),
  triggerSos: (rideId: string, location?: { latitude: number; longitude: number }) =>
    apiFetch<SosAlert>(`/rides/${rideId}/sos`, {
      method: 'POST',
      body: location ?? {},
    }),
  clearSos: (rideId: string) =>
    apiFetch<{ alert: SosAlert | null }>(`/rides/${rideId}/sos/clear`, { method: 'POST' }),
  respondSos: (rideId: string, sosId: string) =>
    apiFetch<{ responseCount: number }>(`/rides/${rideId}/sos/${sosId}/respond`, { method: 'POST' }),
};

export function useBroadcasts(rideId: string | undefined, refetchMs?: number) {
  return useQuery({
    queryKey: ['rides', 'broadcasts', rideId ?? ''],
    queryFn: () => activityApi.listBroadcasts(rideId!),
    enabled: Boolean(rideId),
    refetchInterval: refetchMs,
  });
}

export function useSendBroadcast(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => activityApi.sendBroadcast(rideId, message),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rides', 'broadcasts', rideId] }),
  });
}

export function useChangeStatus(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      status,
      reason,
      startLocation,
    }: {
      status: RideStatus;
      reason?: string;
      startLocation?: { latitude: number; longitude: number };
    }) => activityApi.changeStatus(rideId, status, reason, startLocation),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
      qc.invalidateQueries({ queryKey: rideKeys.mine });
      qc.invalidateQueries({ queryKey: ['rides', 'participants', rideId] });
    },
  });
}

export function useCheckinProgress(rideId: string | undefined, refetchMs?: number) {
  return useQuery({
    queryKey: ['rides', 'checkin-progress', rideId ?? ''],
    queryFn: () => activityApi.checkinProgress(rideId!),
    enabled: Boolean(rideId),
    refetchInterval: refetchMs,
  });
}

export function useMyCheckins(rideId: string | undefined) {
  return useQuery({
    queryKey: ['rides', 'my-checkins', rideId ?? ''],
    queryFn: () => activityApi.myCheckins(rideId!),
    enabled: Boolean(rideId),
  });
}

export function useCheckIn(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      meetingPointId: string;
      latitude: number;
      longitude: number;
      checkinMethod?: 'manual' | 'geofence';
    }) => activityApi.checkIn(rideId, args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rides', 'my-checkins', rideId] });
      qc.invalidateQueries({ queryKey: ['rides', 'checkin-progress', rideId] });
    },
  });
}

export function useMySos(rideId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['rides', 'sos', 'mine', rideId ?? ''],
    queryFn: () => activityApi.mySos(rideId!),
    enabled: Boolean(rideId) && enabled,
    refetchInterval: enabled ? 15000 : undefined,
  });
}

export function useTriggerSos(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (location?: { latitude: number; longitude: number }) =>
      activityApi.triggerSos(rideId, location),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rides', 'sos', 'mine', rideId] }),
  });
}

export function useClearSos(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => activityApi.clearSos(rideId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rides', 'sos', 'mine', rideId] }),
  });
}
